import type { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { spaClick } from '@/components/ui/navigation-rail'
import { HOME_ROUTE } from './sections'

type BrandHomeLinkProps = Omit<ComponentProps<'a'>, 'href'> & {
  /** Runs after a plain click, whether or not the route changed (a drawer closes itself). */
  onNavigate?: () => void
}

/**
 * The brand as the way home: a link to the app's landing page that names itself
 * for screen readers and knows when that page is already the one open. What it
 * shows — the mark, the lockup — is the caller's.
 */
export function BrandHomeLink({ onNavigate, onClick, children, ...props }: BrandHomeLinkProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const atHome = pathname === HOME_ROUTE
  return (
    <a
      aria-label={`Xendrel — ${t('nav.cases.home')}`}
      aria-current={atHome ? 'page' : undefined}
      {...props}
      href={HOME_ROUTE}
      onClick={e => {
        onClick?.(e)
        spaClick(e, () => {
          // Already home: nothing to push onto the history.
          if (!atHome) navigate(HOME_ROUTE)
          onNavigate?.()
        })
      }}
    >
      {children}
    </a>
  )
}
